import { useState, useEffect, useCallback, useRef } from 'react';
import socketService from '../../services/socket';
import { api } from '../../services/api';
import { uploadMedia } from '../../utils/cloudinary';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import './ChatWidget.css';

function Chat({
  conversationId,
  currentUser,
  conversation,
  canSendManualEmail = false,
  onNotifyParticipant,
  notifyLoading = false,
  notifiedMessageIds = [],
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typingUsers, setTypingUsers] = useState([]);
  const [gifsEnabled, setGifsEnabled] = useState(false);
  const processedMessages = useRef(new Set());

  // GIF search only shows once it's set up on the server.
  useEffect(() => {
    let live = true;
    api
      .gifsEnabled()
      .then((on) => live && setGifsEnabled(on))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const msgs = await api.getMessages(conversationId);
      setMessages(msgs);
      setLoading(false);
    } catch (error) {
      console.error('Error loading messages:', error);
      setLoading(false);
    }
  }, [conversationId]);

  // Socket connection management - connect on mount only
  useEffect(() => {
    socketService.connect();

    // Don't disconnect or remove listeners on unmount
    // The socket should persist across component lifecycles
    return () => {
      // Cleanup handled in conversation-specific useEffect
    };
  }, []);

  // Conversation-specific logic - join/leave conversation and handle events
  useEffect(() => {
    if (!conversationId) return;

    // Capture ref at the start of the effect
    const currentProcessedMessages = processedMessages.current;

    // Join conversation
    socketService.joinConversation(conversationId);

    // Load initial messages
    loadMessages();

    // Mark conversation as read when opened
    const markAsRead = async () => {
      try {
        await api.markAsRead(conversationId);
      } catch (error) {
        console.error('Error marking conversation as read:', error);
      }
    };
    markAsRead();

    // Listen for new messages with duplicate prevention
    const handleReceiveMessage = (message) => {
      try {
        // A coach's socket sits in every client's room (the inbox needs
        // live updates), so this listener hears all of them. Without this
        // check, a message from one client showed up inside another
        // client's open thread.
        if (
          message.conversationId &&
          String(message.conversationId) !== String(conversationId)
        ) {
          return;
        }

        // Ignore our own messages since we already added them optimistically
        if (message.senderId === currentUser.id) {
          return;
        }

        if (currentProcessedMessages.has(message._id || message.tempId)) {
          return;
        }

        currentProcessedMessages.add(message._id || message.tempId);
        setMessages((prev) => [...prev, message]);
        // Seen live in the open thread → read. Otherwise it stayed unread
        // and badged the inbox (here and in the coach app) for a message
        // already on screen.
        if (document.visibilityState === 'visible') {
          api.markAsRead(conversationId).catch(() => {});
        }
      } catch (error) {
        console.error('❌ Error handling received message:', error);
      }
    };

    socketService.onReceiveMessage(handleReceiveMessage);

    // The server's copy replaces our optimistic one (✓, real media URL).
    const handleSent = ({ tempId, message } = {}) => {
      if (!message || String(message.conversationId) !== String(conversationId)) return;
      currentProcessedMessages.add(message._id);
      setMessages((prev) =>
        prev.map((m) => (m.tempId === tempId ? { ...message, tempId, status: 'sent' } : m))
      );
    };
    // The server refused it (e.g. a file it won't accept).
    const handleError = ({ tempId, error } = {}) => {
      setMessages((prev) =>
        prev.map((m) => (m.tempId === tempId ? { ...m, status: 'failed', error } : m))
      );
    };
    const handleTypingEvent = (data) => {
      if (data.userId !== currentUser.id) {
        setTypingUsers((prev) =>
          prev.find((u) => u.userId === data.userId) ? prev : [...prev, data]
        );
      }
    };
    const handleStopTypingEvent = (data) => {
      setTypingUsers((prev) => prev.filter((u) => u.userId !== data.userId));
    };
    socketService.on('message_sent', handleSent);
    socketService.on('message_error', handleError);
    socketService.on('user_typing', handleTypingEvent);
    socketService.on('user_stop_typing', handleStopTypingEvent);

    return () => {
      // Leave conversation on cleanup
      socketService.leaveConversation(conversationId);

      // Remove exactly the listeners this conversation added (they used to
      // pile up with every conversation switch).
      if (socketService.socket) {
        socketService.socket.off('receive_message', handleReceiveMessage);
      }
      socketService.off('message_sent', handleSent);
      socketService.off('message_error', handleError);
      socketService.off('user_typing', handleTypingEvent);
      socketService.off('user_stop_typing', handleStopTypingEvent);

      // Clear processed messages for this conversation
      currentProcessedMessages.clear();
    };
  }, [conversationId, loadMessages, currentUser.id]);

  const handleSendMessage = async (text) => {
    if (!text.trim()) {
      return; // Don't send empty messages
    }

    const messageData = {
      conversationId,
      senderId: currentUser.id,
      senderName: currentUser.name,
      text: text.trim(),
      tempId: Date.now(),
    };

    // Add optimistic update for better UX
    const optimisticMessage = {
      ...messageData,
      _id: messageData.tempId,
      createdAt: new Date().toISOString(),
      status: 'sending',
    };

    // Add optimistic update for better UX
    setMessages((prev) => [...prev, optimisticMessage]);

    if (socketService.isConnected()) {
      socketService.sendMessage(messageData);
      return;
    }

    // The live connection is down (server restart, laptop sleep, network
    // blip). Send over REST instead — it saves, notifies and fans out to the
    // other side's open thread the same way — and let the socket reconnect.
    // This used to alert "Connection lost" and drop the typed message.
    socketService.connect();
    const markAs = (patch) =>
      setMessages((prev) =>
        prev.map((m) =>
          m.tempId === messageData.tempId ? { ...m, ...patch } : m
        )
      );
    try {
      const saved = await api.sendMessage(conversationId, messageData.text);
      if (saved?._id) processedMessages.current.add(saved._id);
      markAs({ ...saved, status: 'sent' });
    } catch (error) {
      console.error('Error sending message:', error);
      markAs({ status: 'failed' });
    }
  };

  // A photo, video, voice message or GIF: show it right away from the local
  // file, upload it into this conversation's chat folder, then send it like
  // a text message (socket, or REST while the socket reconnects).
  const handleSendMedia = async ({ type, file, localUrl, mediaUrl, media }) => {
    const tempId = Date.now();
    setMessages((prev) => [
      ...prev,
      {
        _id: tempId,
        tempId,
        conversationId,
        senderId: currentUser.id,
        senderName: currentUser.name,
        type,
        text: '',
        mediaUrl: mediaUrl || localUrl,
        media,
        createdAt: new Date().toISOString(),
        status: 'sending',
      },
    ]);
    const markAs = (patch) =>
      setMessages((prev) => prev.map((m) => (m.tempId === tempId ? { ...m, ...patch } : m)));
    try {
      const url = file
        ? await uploadMedia(file, 'chat', {
            conversationId,
            resource: type === 'image' || type === 'video' ? type : 'audio',
            timeoutMs: type === 'video' ? 300000 : 120000,
          })
        : mediaUrl;
      const payload = { type, mediaUrl: url, media };
      if (socketService.isConnected()) {
        socketService.sendMessage({ conversationId, tempId, ...payload });
        return;
      }
      socketService.connect();
      const saved = await api.sendMessage(conversationId, payload);
      if (saved?._id) processedMessages.current.add(saved._id);
      markAs({ ...saved, status: 'sent' });
    } catch (error) {
      console.error('Error sending media:', error);
      const msg = error?.response?.data?.error;
      markAs({ status: 'failed', error: msg });
    }
  };

  const handleTyping = () => {
    socketService.emitTyping(conversationId, currentUser.id, currentUser.name);
  };

  const handleStopTyping = () => {
    socketService.emitStopTyping(conversationId, currentUser.id);
  };

  const handleNotifyFromMessage = useCallback(
    (message) => {
      if (!canSendManualEmail || !onNotifyParticipant) {
        return;
      }

      const messageId = message?._id || message?.tempId;
      const alreadyNotified =
        messageId && notifiedMessageIds.includes(messageId);

      if (alreadyNotified) {
        const confirmResend = window.confirm(
          'This message was already sent in an email. Would you like to send again?'
        );
        if (!confirmResend) {
          return;
        }
        onNotifyParticipant(true, message);
      } else {
        onNotifyParticipant(false, message);
      }
    },
    [canSendManualEmail, onNotifyParticipant, notifiedMessageIds]
  );

  if (loading) {
    return <div className='chat-loading'>Loading...</div>;
  }

  return (
    <div className='chat-container'>
      {/* Messages */}
      <MessageList
        messages={messages}
        currentUserId={currentUser.id}
        canSendManualEmail={canSendManualEmail}
        onSendNotification={handleNotifyFromMessage}
        notifyLoading={notifyLoading}
        notifiedMessageIds={notifiedMessageIds}
      />

      {/* Typing Indicator */}
      {typingUsers.length > 0 && (
        <div className='typing-indicator'>
          {typingUsers.map((u) => u.userName).join(', ')}{' '}
          {typingUsers.length === 1 ? 'is' : 'are'} typing...
        </div>
      )}

      {/* Input */}
      <MessageInput
        onSendMessage={handleSendMessage}
        onSendMedia={handleSendMedia}
        onTyping={handleTyping}
        onStopTyping={handleStopTyping}
        gifsEnabled={gifsEnabled}
      />
    </div>
  );
}

export default Chat;
